import { useEffect, useState } from 'react';
import { courseTemplates } from '../../../../data/courseTemplates';
import { applyCourseTemplate } from '../../../../utils/applyCourseTemplate';
import { getOrgTemplatesApi, getOrgTemplateApi } from '../../../../api/apiServices';

// Shown when starting a brand-new course: pick a blank course, one of the
// platform's ready-made templates, or (when an organization is selected in
// the admin sidebar) one of that organization's own saved templates. Picking
// a template runs the same section/lecture/quiz creation pipeline used
// everywhere else (applyCourseTemplate), then hands the new course id back
// to the caller to navigate into the full editor.
export default function TemplateGalleryModal({ organizationId, organizationName, instructorName, onClose, onCreated }) {
  const [orgTemplates, setOrgTemplates] = useState([]);
  const [loadingOrgTemplates, setLoadingOrgTemplates] = useState(false);
  const [applying, setApplying] = useState(false);
  const [progressLabel, setProgressLabel] = useState('');
  const [error, setError] = useState('');
  const [partialCourseId, setPartialCourseId] = useState(null);

  useEffect(() => {
    if (!organizationId) {
      setOrgTemplates([]);
      return;
    }
    let cancelled = false;
    setLoadingOrgTemplates(true);
    getOrgTemplatesApi(organizationId)
      .then((res) => {
        if (!cancelled) setOrgTemplates(res?.templates || []);
      })
      .catch((err) => {
        console.error('Failed to load organization templates:', err);
        if (!cancelled) setOrgTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingOrgTemplates(false);
      });
    return () => { cancelled = true; };
  }, [organizationId]);

  const runTemplate = async (template) => {
    setError('');
    setPartialCourseId(null);
    setApplying(true);
    try {
      const courseId = await applyCourseTemplate(
        template,
        instructorName || 'Instructor',
        ({ label }) => setProgressLabel(label),
        organizationId || null
      );
      onCreated(courseId);
    } catch (err) {
      console.error('Failed to create course from template:', err);
      setError(err.message || 'Something went wrong while creating the course.');
      if (err.courseId) setPartialCourseId(err.courseId);
      setApplying(false);
    }
  };

  const useGlobalTemplate = (template) => runTemplate(template);

  const useOrgTemplate = async (summary) => {
    setError('');
    setApplying(true);
    setProgressLabel('Loading template...');
    try {
      const res = await getOrgTemplateApi(summary._id);
      if (!res?.success || !res?.template?.structure) {
        throw new Error(res?.message || 'Failed to load that template');
      }
      await runTemplate(res.template.structure);
    } catch (err) {
      console.error('Failed to load organization template:', err);
      setError(err.message || 'Failed to load that template');
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Start your course</h2>
            <p className="text-xs text-slate-500 mt-0.5">Begin from scratch or clone an existing structure</p>
          </div>
          {!applying && (
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-2xl">
              <i className="ri-close-line text-lg" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {applying ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              {error ? (
                <>
                  <div className="w-14 h-14 flex items-center justify-center bg-rose-50 text-rose-500 rounded-full mb-4">
                    <i className="ri-error-warning-line text-2xl" />
                  </div>
                  <p className="text-sm font-semibold text-slate-800 mb-1">Couldn't finish creating the course</p>
                  <p className="text-xs text-slate-500 max-w-sm mb-5">{error}</p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setApplying(false); setError(''); }}
                      className="px-4 py-2 bg-slate-100 text-slate-600 text-xs font-semibold rounded-xl hover:bg-slate-200"
                    >
                      Back to templates
                    </button>
                    {partialCourseId && (
                      <button
                        onClick={() => onCreated(partialCourseId)}
                        className="px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-xl hover:bg-teal-700"
                      >
                        Finish it manually
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 flex items-center justify-center bg-teal-50 text-teal-600 rounded-full mb-4 animate-pulse">
                    <i className="ri-loader-4-line text-2xl animate-spin" />
                  </div>
                  <p className="text-sm font-semibold text-slate-800">{progressLabel || 'Working...'}</p>
                  <p className="text-xs text-slate-400 mt-1">This can take a moment for templates with many lessons.</p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-8">
              {/* Blank course */}
              <button
                onClick={onClose}
                className="w-full flex items-center gap-4 p-5 border-2 border-dashed border-slate-200 rounded-2xl hover:border-teal-400 hover:bg-teal-50/40 transition-colors text-left"
              >
                <div className="w-11 h-11 flex items-center justify-center bg-slate-100 rounded-2xl flex-shrink-0">
                  <i className="ri-file-add-line text-xl text-slate-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Start from a blank course</p>
                  <p className="text-xs text-slate-500 mt-0.5">Build the structure yourself, one section and lecture at a time</p>
                </div>
              </button>

              {/* Org templates */}
              {organizationId && (
                <div>
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
                    {organizationName ? `${organizationName}'s templates` : "Your organization's templates"}
                  </h3>
                  {loadingOrgTemplates ? (
                    <p className="text-xs text-slate-400">Loading...</p>
                  ) : orgTemplates.length === 0 ? (
                    <p className="text-xs text-slate-400">
                      No templates saved yet. You can save any course as a template for your organization from its editor.
                    </p>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-3">
                      {orgTemplates.map((tpl) => (
                        <button
                          key={tpl._id}
                          onClick={() => useOrgTemplate(tpl)}
                          className="text-left p-4 border border-slate-200 rounded-2xl hover:border-teal-400 hover:shadow-sm transition-all"
                        >
                          <div className="flex items-center gap-2 mb-1.5">
                            <i className="ri-building-line text-purple-500" />
                            <p className="text-sm font-semibold text-slate-800 truncate">{tpl.name}</p>
                          </div>
                          <p className="text-xs text-slate-500 line-clamp-2">{tpl.description || 'No description'}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Global (platform) templates */}
              <div>
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Ready-made templates</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {courseTemplates.map((tpl) => (
                    <button
                      key={tpl.id}
                      onClick={() => useGlobalTemplate(tpl)}
                      className="text-left p-4 border border-slate-200 rounded-2xl hover:border-teal-400 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <i className="ri-magic-line text-teal-500" />
                        <p className="text-sm font-semibold text-slate-800 truncate">{tpl.title}</p>
                      </div>
                      <p className="text-xs text-slate-500 line-clamp-2">{tpl.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
